import type { Field } from '@formily/core';
import { useState } from 'react';
import { useField, useForm } from '@formily/react';
import { observer } from '@formily/reactive-react';
import { usePage } from '../../context/PageContext';
import { useNavigate } from 'react-router-dom';
import { stripEmptyCollections } from '../../utils/formFormatters';
import SimpleTriggerActionButton, {
  type ISimpleTriggerActionButtonProps,
} from '../simple-trigger-action-button';
import { useMessage, useToggleVisibility } from '../../../hooks';
import { parseTemplateString } from '../../utils/data-parser';
import { stripSkipOnSubmitFields } from '../../utils/stripSkipOnSubmitFields';
import ConfirmModal from '../../../components/ConfirmModal';
import useResolveModelVersionId from './useResolveModelVersionId';
import { searchJmesPath } from '../../../utils';
import { removeInternalValues } from './utils';

export interface IFormTriggerActionButtonProps
  extends ISimpleTriggerActionButtonProps {
  action?: string;
  transformer?: string;
  confirm?: {
    show?: boolean;
    title?: string;
    message?: string;
    okButtonText?: string;
    cancelButtonText?: string;
  };
}

const FormTriggerActionButton = observer(() => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    isVisible: isConfirmVisible,
    show: showConfirm,
    hide: hideConfirm,
  } = useToggleVisibility();

  const field = useField<Field>();
  const form = useForm();

  const componentProps = field.componentProps || {};
  const {
    text,
    action,
    transformer,
    visible,
    onSuccessMessage,
    onErrorMessage,
    navigateOnSuccess,
    icon,
    confirm,
    ...rest
  } = componentProps;

  const { disabled } = field || {};

  const pageData = usePage();

  const { resolveModelVersionId } = useResolveModelVersionId();

  const message = useMessage();
  const navigate = useNavigate();

  const handleTriggerAction = async () => {
    const formValues = stripSkipOnSubmitFields(form);

    const { props } = form;
    const parentContext = (props as any).parentContext || {};

    const cleanedFormValues = removeInternalValues(formValues);
    let data = cleanedFormValues;

    if (transformer) {
      data = searchJmesPath(
        { ...parentContext, ...pageData, form: cleanedFormValues },
        transformer
      );
    }
    data = stripEmptyCollections(data);

    const modelVersionId = await resolveModelVersionId(data);
    // We remove local values one more time in case jmspath added
    data = removeInternalValues(data);

    if (pageData.executeAction) {
      setIsLoading(true);
      pageData.executeAction(
        {
          data,
          url: `models/${modelVersionId}/actions/${action}/execute`,
        },
        {
          onSuccess: (response: any) => {
            setIsLoading(false);
            if (onSuccessMessage) {
              message.success(onSuccessMessage);
            }
            if (navigateOnSuccess) {
              const url = parseTemplateString(navigateOnSuccess, {
                ...pageData,
                response,
              });
              if (url) {
                navigate(url);
              }
            }
            if (isConfirmVisible) {
              hideConfirm();
            }
          },
          onError: () => {
            setIsLoading(false);
            if (onErrorMessage) {
              message.error(onErrorMessage);
            }
          },
        }
      );
    }
  };

  const handleValidateForm = async () => {
    try {
      await form.validate();
      if (!form.valid) {
        return false;
      }
      return true;
    } catch (error) {
      console.error('Validation failed:', error);
      return false;
    }
  };

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const isValid = await handleValidateForm();
    if (!isValid) {
      return;
    }
    if (confirm?.show) {
      showConfirm();
    } else {
      handleTriggerAction();
    }
  };

  if (visible === false) return null;

  return (
    <>
      <SimpleTriggerActionButton
        text={text}
        visible={visible}
        loading={
          Boolean(pageData?.meta?.isExecuting) && isLoading && !isConfirmVisible
        }
        onClick={handleClick}
        disabled={disabled}
        icon={icon}
        {...rest}
      />
      {isConfirmVisible && (
        <ConfirmModal
          isVisible={isConfirmVisible}
          title={confirm?.title}
          children={
            confirm?.message || 'Are you sure you want to perform this action?'
          }
          okButtonText={confirm?.okButtonText || 'Confirm'}
          cancelButtonText={confirm?.cancelButtonText || 'Cancel'}
          onConfirm={handleTriggerAction}
          onCancel={hideConfirm}
          isConfirming={Boolean(pageData?.meta?.isExecuting) && isLoading}
        />
      )}
    </>
  );
});

export default FormTriggerActionButton;
